/**
 * 병사 이름. 삼국시대 사람 이름의 결을 따른다.
 *
 * 병사는 계정에 한 명씩 쌓이고, 죽으면 돌아오지 않는다. 숫자로만 셀 때는 잃는 것이
 * 재건 비용이었는데, 이름이 붙으면 "누가" 쓰러졌는지가 된다. 아군을 작게, 적을 크게
 * 가기로 하면서(병사 한 명 한 명이 무게를 갖는 쪽) 모든 병사에게 이름을 붙인다.
 *
 * 기록에 남은 이름(거칠부 居柒夫, 이사부 異斯夫, 을두지 乙豆智, 사다함 斯多含, 비령자
 * 丕寧子, 흑치 黑齒 등)처럼 성 없이 두세 음절이고, 끝소리에 나라마다 버릇이 있다.
 * 고구려는 -두·-루·-달, 백제는 -사·-진·-복, 신라는 -부·-지·-함 쪽이 잦다.
 *
 * **시뮬레이션 밖이다.** 계정에만 있고 리플레이에 실리지 않는다. 그래도 같은 병사는
 * 늘 같은 이름이 나오게 uid에서 정한다(`Math.random`을 쓰지 않는다).
 */
import { Culture } from '../sim/troops.ts';

const FIRST = [
  '거', '사', '비', '밀', '유', '두', '마', '개', '소', '아', '무', '해', '가', '구', '노',
  '도', '을', '온', '연', '석', '우', '기', '모', '차', '탈', '흑', '계', '고', '달', '미',
] as const;

const MIDDLE = [
  '칠', '다', '령', '문', '로', '진', '수', '막', '치', '벌', '흘', '이', '동', '백', '마', '대',
] as const;

const ENDING: Record<Culture, readonly string[]> = {
  [Culture.Goguryeo]: ['두', '루', '달', '우', '지', '소', '모', '부'],
  [Culture.Baekje]: ['사', '진', '복', '신', '충', '수', '지', '로'],
  [Culture.Silla]: ['부', '지', '함', '자', '종', '창', '랑', '보'],
};

/**
 * 요즘 낱말과 겹쳐 이름으로 읽히지 않는 것. 처음 뽑아 보니 "거부", "흑자", "기복",
 * "차창", "소지"가 나왔다 — 병사가 "거부"라면 이름이 아니라 대답으로 읽힌다.
 */
const AVOID = new Set([
  '거부', '흑자', '기복', '차창', '소지', '마사', '두부', '마부', '고부', '부자', '사자',
  '가지', '모자', '기지', '미지', '아부', '가부', '무지', '고지', '도사', '마수', '소수',
  '기사', '사수', '우수', '도수', '가수', '무수', '아사', '고사', '사사', '마지', '기부',
  '도부', '두지', '구지', '노사', '모사', '소사', '우사', '비사', '고수', '구수', '미수',
  '사부', '우부', '가사', '구두', '모두', '기우', '소모', '가랑', '사랑', '도랑', '고랑',
  '소보', '구보', '도보', '사보', '아보', '비보', '기보', '마진', '고진', '도진', '사진',
  '소진', '가진', '기진', '우진', '미진', '고로', '가로', '도로', '사로', '기로', '마로',
  '고충', '사신', '가신', '기신', '도신', '고신', '우신', '미신', '무신', '소신', '고함',
  '가함', '도함', '기함', '마함', '소함', '사창', '고창', '가창', '도창', '기창', '합창',
  '마종', '사종', '고종', '가종', '도종', '기종', '소종', '무종', '미종', '두루', '누루',
  '마루', '고루', '가루', '도루', '기루', '소루', '사루', '비루',
]);

/** 32비트 정수 섞기(mulberry32의 한 걸음). 같은 입력이면 늘 같은 값이다. */
function mix(n: number): number {
  let t = (n + 0x6d2b79f5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return (t ^ (t >>> 14)) >>> 0;
}

/** 한 번 뽑기. attempt가 다르면 다른 이름이 나온다. */
function draw(culture: Culture, uid: number, attempt: number): string {
  const h = mix(uid * 7919 + attempt * 104729 + culture * 31);
  const ends = ENDING[culture] ?? ENDING[Culture.Goguryeo];
  const first = FIRST[h % FIRST.length];
  const end = ends[(h >>> 8) % ends.length];
  // 셋 중 둘은 세 음절, 하나는 두 음절. 기록의 이름도 세 음절이 많다.
  if ((h >>> 16) % 3 === 0) return first + end;
  const mid = MIDDLE[(h >>> 20) % MIDDLE.length];
  return first + mid + end;
}

/** 이름으로 읽히는가. 낱말과 겹치거나 첫 음절과 끝 음절이 같으면("달막달") 버린다. */
function readable(name: string): boolean {
  return !AVOID.has(name) && name[0] !== name[name.length - 1];
}

/**
 * 병사 이름. 계정 안에서 겹치지 않게 `taken`에 있는 이름은 피한다. 다 겹치면(있을 수
 * 없는 수지만) 뒤에 번호를 붙인다.
 */
export function soldierName(culture: Culture, uid: number, taken: ReadonlySet<string>): string {
  for (let attempt = 0; attempt < 64; attempt++) {
    const name = draw(culture, uid, attempt);
    if (taken.has(name) || !readable(name)) continue;
    return name;
  }
  let k = 2;
  const base = draw(culture, uid, 0);
  while (taken.has(`${base}${k}`)) k++;
  return `${base}${k}`;
}
